import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Send } from "lucide-react";
import type { SalesCensus, SalesCensusMember } from "../types";
import { getApi } from "../api";
import { useAsyncResource } from "../hooks/useAsyncResource";
import { useEnsureGroup } from "../hooks/useEnsureGroup";
import { usePageSetup } from "../hooks/usePageSetup";
import { useGroupStore } from "../store/groupStore";
import { PageHeader } from "../components/PageHeader";
import { Alert } from "../components/Alert";
import { Button } from "../components/Button";
import { Card } from "../components/Card";
import { DataGrid, type GridColumn } from "../components/DataGrid";
import { EmptyState, ErrorState, SkeletonStatStrip } from "../components/ScreenStates";
import { CheckboxInput } from "../components/fields/CheckboxInput";
import { formatDate, formatDateTime, formatInteger, formatRmm } from "../utils/format";

interface FigureProps {
  label: string;
  asSent: string;
  whatIf: string | null;
  changed: boolean;
}

function Figure({ label, asSent, whatIf, changed }: FigureProps) {
  return (
    <div className="rounded-2xl bg-white p-4 shadow-md">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums text-brand-navy">{whatIf ?? asSent}</p>
      <p className="text-xs text-gray-500">{changed ? `As sent: ${asSent}` : "Matches what was sent"}</p>
    </div>
  );
}

/**
 * What Sales sees after Send to Sales: the census with one line per enrolled member and their total debits,
 * plus the group figures. The checkboxes are the Sales-side what-if. Unticking a member asks the workbench
 * API how the group would rate without them; nothing about the group changes.
 */
export function SentCensusPage() {
  const { groupId = "" } = useParams();
  const navigate = useNavigate();
  const ensure = useEnsureGroup(groupId);
  const group = useGroupStore((state) => (state.group?.id === groupId ? state.group : null));
  const census = useAsyncResource(() => getApi().getSentCensus(groupId), groupId, "SentCensusPage.census");
  const [unticked, setUnticked] = useState<readonly string[]>([]);
  const breadcrumb = useMemo(() => (group ? [{ label: "Recent Groups", to: "/uw/recent" }, { label: group.name, to: `/uw/groups/${group.id}`, status: group.status }, { label: "Sent census", to: "" }] : null), [group]);
  usePageSetup("Sent census", breadcrumb);

  const includedIds = useMemo(() => (census.data?.members ?? []).map((member) => member.censusMemberId).filter((id) => !unticked.includes(id)), [census.data, unticked]);
  const isWhatIf = unticked.length > 0;
  const preview = useAsyncResource(async () => (isWhatIf ? getApi().previewAdjustedRmm(groupId, { includedCensusMemberIds: includedIds }) : null), `${groupId}|${includedIds.join(",")}`, "SentCensusPage.preview");

  const columns = useMemo<GridColumn<SalesCensusMember>[]>(
    () => [
      {
        key: "enrolled",
        header: "Enrolling",
        align: "center",
        widthClassName: "w-24",
        render: (member) => <CheckboxInput id={`enrolled-${member.censusMemberId}`} label={`Enrolling: ${member.fullName}`} hideLabel checked={!unticked.includes(member.censusMemberId)} onChange={(checked) => setUnticked((current) => (checked ? current.filter((id) => id !== member.censusMemberId) : [...current, member.censusMemberId]))} />,
      },
      { key: "name", header: "Member", sortValue: (member) => member.fullName, render: (member) => <span className={unticked.includes(member.censusMemberId) ? "text-gray-400 line-through" : "font-medium text-gray-800"}>{member.fullName}</span> },
      { key: "relation", header: "Relation", render: (member) => member.relation },
      { key: "dob", header: "DOB", render: (member) => formatDate(member.dateOfBirth) || <span className="text-gray-400">–</span> },
      { key: "debits", header: "Total Debits", align: "right", sortValue: (member) => member.totalDebits, render: (member) => <strong>{formatInteger(member.totalDebits)}</strong> },
    ],
    [unticked],
  );

  if (ensure.error) return <ErrorState message={ensure.error} onRetry={ensure.retry} />;
  if (census.state.kind === "error") return <ErrorState message={census.state.message} onRetry={census.reload} />;
  if (!group || !census.data) {
    return (
      <>
        <PageHeader title="Sent census" />
        {census.state.kind === "ready" && !census.data ? <EmptyState icon={Send} message="This version has not been sent to Sales yet." action={<Button variant="secondary" onClick={() => navigate(`/uw/groups/${groupId}`)}>Back to group</Button>} /> : <SkeletonStatStrip tiles={3} />}
      </>
    );
  }

  const sent: SalesCensus = census.data;
  const figures = preview.data;

  return (
    <>
      <PageHeader
        title={`${group.name}: census sent to Sales`}
        subtitle={`Version ${sent.version}, sent ${formatDateTime(sent.sentUtc)} by ${sent.sentByName}. ${sent.members.length} enrolled member${sent.members.length === 1 ? "" : "s"}.`}
        actions={
          <Button variant="secondary" icon={ArrowLeft} onClick={() => navigate(`/uw/groups/${group.id}`)}>
            Back to group
          </Button>
        }
      />
      <div className="space-y-4">
        <Alert type="info" showIcon message="This is what Sales sees in Salesforce: each enrolled member with their total debit points, and the group's RMM. No conditions, ratings, or medications are shared. Unticking a member is a what-if for Sales and changes nothing in this group." />
        <div className="grid gap-3 md:grid-cols-3">
          <Figure label="Total Debits" asSent={formatInteger(sent.totalDebits)} whatIf={figures ? formatInteger(figures.totalDebits) : null} changed={isWhatIf} />
          <Figure label="RMM" asSent={formatRmm(sent.rmm)} whatIf={figures ? formatRmm(figures.rmm) : null} changed={isWhatIf} />
          <Figure label="Adjusted RMM" asSent={formatRmm(sent.adjustedRmm)} whatIf={figures ? formatRmm(figures.adjustedRmm) : null} changed={isWhatIf} />
        </div>
        <Card title="Census" actions={isWhatIf ? <Button variant="ghost" size="sm" onClick={() => setUnticked([])}>Reset to as sent</Button> : <span className="text-xs text-gray-500">Untick a member to see the group re-rate</span>}>
          <DataGrid caption="Census sent to Sales" columns={columns} rows={sent.members} rowKey={(member) => member.censusMemberId} density="compact" defaultSort={{ key: "name", direction: "asc" }} isRowExcluded={(member) => unticked.includes(member.censusMemberId)} />
        </Card>
      </div>
    </>
  );
}
